import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/common/StatusBadge";
import { Plus, Trash2, User, Building2, UserCircle } from "lucide-react";
import { formatEuro, formatPercent, formatDateFr } from "@/lib/format";
import { toast } from "sonner";
import { HOLDER_TYPE_LABELS } from "@/constants/group";

const EMPTY = {
  company_id: null, holder_type: "person", holder_company_id: "",
  holder_first_name: "", holder_last_name: "", holder_external_name: "",
  ownership_pct: 0, shares_count: null, nominal_value: null, voting_rights_pct: null,
  detention_type: "direct", start_date: "", end_date: "", notes: "", is_draft: false,
};

export default function CompanyShareholdersTab({ company }) {
  const [owned, setOwned] = useState([]);        // participations in this company
  const [ownedBy, setOwnedBy] = useState([]);    // participations this company holds in others
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, company_id: company.id });

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get(`/shareholders?company_id=${company.id}`),
        api.get(`/shareholders?holder_company_id=${company.id}`),
        api.get("/companies"),
      ]);
      setOwned(a.data.items || []);
      setOwnedBy(b.data.items || []);
      setCompanies(c.data.items || []);
    } catch (err) {
      console.error("Load shareholders failed:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company.id]);

  const totalOwned = useMemo(() => owned.filter((s) => !s.end_date).reduce((a, s) => a + Number(s.ownership_pct || 0), 0), [owned]);
  const companyName = (id) => companies.find((c) => c.id === id)?.denomination || companies.find((c) => c.id === id)?.name || "—";

  const create = async () => {
    try {
      const payload = { ...form, company_id: company.id, ownership_pct: parseFloat(form.ownership_pct) };
      if (payload.holder_type !== "company") payload.holder_company_id = null;
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      await api.post("/shareholders", payload);
      toast.success("Participation ajoutée");
      setOpen(false); setForm({ ...EMPTY, company_id: company.id }); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ajout impossible");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette participation ?")) return;
    try { await api.delete(`/shareholders/${id}`); toast.success("Supprimée"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  return (
    <div className="space-y-8">
      {/* Owned in this company */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-overline">Capital de {company.denomination}</div>
            <h3 className="mt-1 font-heading text-xl">Détenteurs — total actuel : {formatPercent(totalOwned)}</h3>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-pill" data-testid="shareholder-add-btn"><Plus className="mr-2 h-4 w-4" /> Ajouter</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvelle participation</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Type de détenteur</Label>
                  <Select value={form.holder_type} onValueChange={(v) => setForm({ ...form, holder_type: v })}>
                    <SelectTrigger data-testid="shareholder-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(HOLDER_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.holder_type === "company" ? (
                  <div>
                    <Label>Société détentrice</Label>
                    <Select value={form.holder_company_id} onValueChange={(v) => setForm({ ...form, holder_company_id: v })}>
                      <SelectTrigger data-testid="shareholder-form-company"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>
                        {companies.filter((c) => c.id !== company.id).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : form.holder_type === "person" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Prénom</Label><Input value={form.holder_first_name} onChange={(e) => setForm({ ...form, holder_first_name: e.target.value })} /></div>
                    <div><Label>Nom</Label><Input value={form.holder_last_name} onChange={(e) => setForm({ ...form, holder_last_name: e.target.value })} /></div>
                  </div>
                ) : (
                  <div>
                    <Label>Nom du tiers externe</Label>
                    <Input value={form.holder_external_name} onChange={(e) => setForm({ ...form, holder_external_name: e.target.value })} />
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>% détention</Label><Input type="number" step="0.01" value={form.ownership_pct} onChange={(e) => setForm({ ...form, ownership_pct: e.target.value })} data-testid="shareholder-form-pct" /></div>
                  <div><Label>Nb parts</Label><Input type="number" value={form.shares_count || ""} onChange={(e) => setForm({ ...form, shares_count: e.target.value ? parseFloat(e.target.value) : null })} /></div>
                  <div><Label>Valeur nominale</Label><Input type="number" value={form.nominal_value || ""} onChange={(e) => setForm({ ...form, nominal_value: e.target.value ? parseFloat(e.target.value) : null })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date d'entrée</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                  <div><Label>Date de sortie</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <Label>Type de détention</Label>
                    <Select value={form.detention_type} onValueChange={(v) => setForm({ ...form, detention_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct">Directe</SelectItem>
                        <SelectItem value="indirect">Indirecte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3 pb-2">
                    <Switch checked={form.is_draft} onCheckedChange={(v) => setForm({ ...form, is_draft: v })} data-testid="shareholder-form-draft" />
                    <Label className="!mt-0">Brouillon (autoriser total &gt; 100 %)</Label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={create} data-testid="shareholder-form-submit">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <ProgressBar total={totalOwned} />

        <div className="card-elevated overflow-hidden mt-4">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-6 py-3">Détenteur</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3 text-right">% détention</th>
                <th className="px-6 py-3 text-right">Parts</th>
                <th className="px-6 py-3">Entrée</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
              {!loading && owned.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                  <User className="mx-auto h-6 w-6 mb-2 text-muted-foreground/60" />
                  Aucun détenteur renseigné.
                </td></tr>
              )}
              {owned.map((s) => (
                <tr key={s.id} data-testid={`shareholder-row-${s.id}`} className="hover:bg-secondary/30">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      {s.holder_type === "company" ? <Building2 className="h-4 w-4 text-muted-foreground" />
                        : s.holder_type === "external" ? <UserCircle className="h-4 w-4 text-muted-foreground" />
                        : <User className="h-4 w-4 text-muted-foreground" />}
                      <span className="font-medium">
                        {s.holder_type === "company" ? companyName(s.holder_company_id)
                          : s.holder_type === "external" ? s.holder_external_name
                          : `${s.holder_first_name || ""} ${s.holder_last_name || ""}`.trim() || "—"}
                      </span>
                      {s.is_draft && <StatusBadge tone="warning">Brouillon</StatusBadge>}
                      {s.end_date && <StatusBadge tone="neutral">Cédée</StatusBadge>}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-xs text-muted-foreground">{HOLDER_TYPE_LABELS[s.holder_type]}</td>
                  <td className="px-6 py-3 text-right font-mono">{formatPercent(s.ownership_pct)}</td>
                  <td className="px-6 py-3 text-right font-mono text-xs">{s.shares_count ? Math.round(s.shares_count) : "—"}</td>
                  <td className="px-6 py-3 text-xs text-muted-foreground">{s.start_date ? formatDateFr(s.start_date) : "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => del(s.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Owned by (participations of this company in others) */}
      <div>
        <div className="text-overline">Participations détenues par {company.denomination}</div>
        <h3 className="mt-1 font-heading text-xl mb-4">Sociétés dans lesquelles cette entité participe</h3>
        <div className="card-elevated overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-6 py-3">Société détenue</th>
                <th className="px-6 py-3 text-right">% détention</th>
                <th className="px-6 py-3">Entrée</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {ownedBy.length === 0 && (
                <tr><td colSpan={3} className="px-6 py-8 text-center text-muted-foreground text-sm">
                  Cette entité ne détient aucune participation.
                </td></tr>
              )}
              {ownedBy.map((s) => (
                <tr key={s.id} className="hover:bg-secondary/30">
                  <td className="px-6 py-3 font-medium">{companyName(s.company_id)}</td>
                  <td className="px-6 py-3 text-right font-mono">{formatPercent(s.ownership_pct)}</td>
                  <td className="px-6 py-3 text-xs text-muted-foreground">{s.start_date ? formatDateFr(s.start_date) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ total }) {
  const clamped = Math.min(total, 100);
  const over = total > 100.01;
  return (
    <div className="mt-4">
      <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
        <div className={`h-full transition-all ${over ? "bg-destructive" : total >= 99.99 ? "bg-emerald-600" : "bg-primary"}`}
             style={{ width: `${clamped}%` }} />
      </div>
      <div className="mt-1 flex justify-between text-xs">
        <span className={over ? "text-destructive" : total >= 99.99 ? "text-emerald-700" : "text-muted-foreground"}>
          {formatPercent(total)} attribués
        </span>
        <span className="text-muted-foreground">{formatEuro(0, false).replace(/\S/g, '')}{Math.max(0, 100 - total).toFixed(1)} % restants</span>
      </div>
    </div>
  );
}
